import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from 'app/providers/Store';
import {
  usersSelectors,
  getUsers,
  getUserById,
  createUser,
  updateUser,
  deleteUser,
  UserListItem,
} from 'entities/users';
import { UserRequestDto } from 'shared/api';
import { useGlobalSpinner } from 'shared/hooks';
import { USER_STATUS_CONFIG } from '../constants/userConstants';

export const useUsers = () => {
  const dispatch = useAppDispatch();
  const users = useAppSelector(usersSelectors.getUsers);
  const isLoading = useAppSelector(usersSelectors.getUsersLoading);
  const userDetail = useAppSelector(usersSelectors.getUserDetail);
  const userDetailLoading = useAppSelector(usersSelectors.getUserDetailLoading);
  const { showSpinner, hideSpinner } = useGlobalSpinner();

  const loadUsers = useCallback(() => {
    showSpinner('Загрузка пользователей...');
    dispatch(getUsers({})).finally(() => {
      hideSpinner();
    });
  }, [dispatch, showSpinner, hideSpinner]);

  const createNewUser = useCallback(async (data: UserRequestDto) => {
    try {
      showSpinner('Создание пользователя...');
      await dispatch(createUser(data)).unwrap();
      loadUsers();
      return { success: true };
    } catch (error) {
      return { success: false, error };
    } finally {
      hideSpinner();
    }
  }, [dispatch, loadUsers, showSpinner, hideSpinner]);

  const updateExistingUser = useCallback(async (id: number, data: UserRequestDto) => {
    try {
      showSpinner('Обновление пользователя...');
      await dispatch(updateUser({ id, data })).unwrap();
      loadUsers();
      return { success: true };
    } catch (error) {
      return { success: false, error };
    } finally {
      hideSpinner();
    }
  }, [dispatch, loadUsers, showSpinner, hideSpinner]);

  const loadUserDetail = useCallback(async (id: number) => {
    try {
      showSpinner('Загрузка данных пользователя...');
      await dispatch(getUserById(id)).unwrap();
      return { success: true } as const;
    } catch (error) {
      return { success: false, error } as const;
    } finally {
      hideSpinner();
    }
  }, [dispatch, showSpinner, hideSpinner]);

  const deleteUserById = useCallback(async (id: number) => {
    try {
      showSpinner('Удаление пользователя...');
      await dispatch(deleteUser(id)).unwrap();
      loadUsers();
      return { success: true };
    } catch (error) {
      return { success: false, error };
    } finally {
      hideSpinner();
    }
  }, [dispatch, loadUsers, showSpinner, hideSpinner]);

  const getStatusInfo = useCallback((user: UserListItem) => {
    const status = user.status as keyof typeof USER_STATUS_CONFIG;
    return USER_STATUS_CONFIG[status] || USER_STATUS_CONFIG.INACTIVE;
  }, []);

  const formatFullName = useCallback((user: UserListItem) => {
    const parts = [user.lastname, user.firstname, user.middleName].filter(Boolean);
    return parts.join(' ');
  }, []);

  const formatCompanyInfo = useCallback((user: UserListItem) => {
    if (user.innCompany) {
      return `${user.company} (ИНН: ${user.innCompany})`;
    }
    return user.company;
  }, []);

  return useMemo(() => ({
    users,
    isLoading,
    loadUsers,
    createNewUser,
    updateExistingUser,
    deleteUserById,
    userDetail,
    userDetailLoading,
    loadUserDetail,
    getStatusInfo,
    formatFullName,
    formatCompanyInfo,
  }), [
    users,
    isLoading,
    loadUsers,
    createNewUser,
    updateExistingUser,
    deleteUserById,
    userDetail,
    userDetailLoading,
    loadUserDetail,
    getStatusInfo,
    formatFullName,
    formatCompanyInfo,
  ]);
};
